import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, ArrowRight, Trash2 } from "lucide-react";
import { api, errMsg, fmtDate } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, ActionButton, EmptyState } from "../../components/common";
import { HorizontalScroll } from "../../components/HorizontalScroll";
import { JobTicketForm } from "../../components/JobTicketForm";

const COLUMNS = ["Scheduled", "Assigned to Service Provider", "Active", "Paused", "Under Review", "Completed"];
const NEXT = {
  "Scheduled": "Assigned to Service Provider",
  "Assigned to Service Provider": "Active",
  "Active": "Completed",
  "Paused": "Active",
};
const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");
const VENDOR_CARD_SKY = "bg-sky-100/70 border-sky-300 hover:border-sky-400";
const VENDOR_CHIP_SKY = "bg-sky-500/10 text-sky-700 border-sky-400/40";
const VENDOR_CARD_ORANGE = "bg-orange-100/70 border-orange-300 hover:border-orange-400";
const VENDOR_CHIP_ORANGE = "bg-orange-500/10 text-orange-700 border-orange-400/40";
const VENDOR_DOT_SKY = "bg-sky-400 border-sky-500";
const VENDOR_DOT_ORANGE = "bg-orange-400 border-orange-500";
const VENDOR_PALETTE = [
  { card: "bg-violet-100/70 border-violet-300 hover:border-violet-400", chip: "bg-violet-500/10 text-violet-700 border-violet-400/40", dot: "bg-violet-400 border-violet-500" },
  { card: "bg-rose-100/70 border-rose-300 hover:border-rose-400", chip: "bg-rose-500/10 text-rose-700 border-rose-400/40", dot: "bg-rose-400 border-rose-500" },
  { card: "bg-teal-100/70 border-teal-300 hover:border-teal-400", chip: "bg-teal-500/10 text-teal-700 border-teal-400/40", dot: "bg-teal-400 border-teal-500" },
  { card: "bg-lime-100/70 border-lime-300 hover:border-lime-400", chip: "bg-lime-500/10 text-lime-700 border-lime-400/40", dot: "bg-lime-400 border-lime-500" },
  { card: "bg-fuchsia-100/70 border-fuchsia-300 hover:border-fuchsia-400", chip: "bg-fuchsia-500/10 text-fuchsia-700 border-fuchsia-400/40", dot: "bg-fuchsia-400 border-fuchsia-500" },
];
const vendorSource = (t) => {
  const m = (t.created_by || "").match(/^Vendor API \((.+)\)$/);
  return m ? m[1] : null;
};
const vendorColorMap = (tickets) => {
  const srcs = [...new Set(tickets.map(vendorSource).filter(Boolean))]
    .filter((s) => !/postman|i-haul/i.test(s))
    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  const map = {};
  srcs.forEach((s, i) => { map[s] = VENDOR_PALETTE[i % VENDOR_PALETTE.length]; });
  return map;
};
const vendorStyle = (t, colors) => {
  const src = vendorSource(t);
  if (!src || ["Completed", "Cancelled"].includes(t.job_status)) return null;
  if (/i-haul/i.test(src)) return { src, card: VENDOR_CARD_ORANGE, chip: VENDOR_CHIP_ORANGE };
  if (/postman/i.test(src)) return { src, card: VENDOR_CARD_SKY, chip: VENDOR_CHIP_SKY };
  const p = colors[src] || VENDOR_PALETTE[0];
  return { src, card: p.card, chip: p.chip };
};

export default function SwdDispatch() {
  const { user } = useAuth();
  const canDispatch = ["admin", "shop_manager", "dispatcher"].includes(user?.role);
  const [tickets, setTickets] = useState([]);
  const [open, setOpen] = useState(false);
  const vColors = vendorColorMap(tickets);
  const legendSources = [...new Set(tickets.map(vendorSource).filter(Boolean))].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  const byStatus = useMemo(() => {
    const g = {};
    COLUMNS.forEach((c) => { g[c] = []; });
    tickets.forEach((t) => { const s = t.job_status || "Active"; if (g[s]) g[s].push(t); });
    return g;
  }, [tickets]);

  const load = () => api.get("/swd/job-tickets").then((r) => setTickets(r.data)).catch((e) => toast.error(errMsg(e)));

  const deleteTicket = async (t) => {
    if (!window.confirm(`Delete ${t.ticket_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/swd/job-tickets/${t.id}`);
      toast.success(`${t.ticket_number} deleted`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, []);

  const setStatus = async (t, status) => {
    try {
      await api.patch(`/swd/job-tickets/${t.id}`, { job_status: status });
      toast.success(`${t.ticket_number} → ${status}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const onDropCard = (e, col) => {
    e.preventDefault();
    e.currentTarget.classList.remove("border-amber-500/60", "bg-amber-500/5");
    const id = e.dataTransfer.getData("text/plain");
    const t = tickets.find((x) => x.id === id);
    if (t && (t.job_status || "Active") !== col) setStatus(t, col);
  };

  const onDragOverCol = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add("border-amber-500/60", "bg-amber-500/5");
  };

  const onDragLeaveCol = (e) => {
    e.currentTarget.classList.remove("border-amber-500/60", "bg-amber-500/5");
  };

  return (
    <div data-testid="swd-dispatch-page">
      <PageHeader title="Hauling Dispatch Board" subtitle="Job tickets from assignment to completion" testId="swd-dispatch-header">
        <div data-testid="swd-board-legend" className="flex flex-col gap-1 px-3 py-2 rounded-md border border-slate-800 bg-slate-900/40">
          {legendSources.map((s) => (
            <span key={s} className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-slate-400">
              <span className={`w-2.5 h-2.5 rounded-full border flex-shrink-0 ${/i-haul/i.test(s) ? VENDOR_DOT_ORANGE : /postman/i.test(s) ? VENDOR_DOT_SKY : (vColors[s]?.dot || "bg-violet-400 border-violet-500")}`} />
              {s}
            </span>
          ))}
          <span className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-slate-400">
            <span className={`w-2.5 h-2.5 rounded-full border flex-shrink-0 ${VENDOR_DOT_SKY}`} />
            Blue = Driver accepted
          </span>
        </div>
        {canDispatch && (
          <ActionButton testId="swd-create-dispatch-button" onClick={() => setOpen(true)}>
            <Plus size={16} /> New Dispatch
          </ActionButton>
        )}
      </PageHeader>

      {tickets.length === 0 ? (
        <EmptyState message="No dispatches scheduled" testId="swd-dispatch-empty" />
      ) : (
        <HorizontalScroll contentClassName="flex gap-4 pb-4" testId="dispatch-board-row">
          {COLUMNS.map((col) => (
            <div
              key={col}
              className="bg-slate-900/40 border border-slate-800 rounded-lg p-3 min-h-[200px] w-72 flex-shrink-0 transition-colors duration-150"
              data-testid={`dispatch-column-${slug(col)}`}
              onDragOver={onDragOverCol}
              onDragLeave={onDragLeaveCol}
              onDrop={(e) => onDropCard(e, col)}
            >
              <div className="flex items-center justify-between px-1 mb-3">
                <span className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400">{col}</span>
                <span className="text-xs font-mono text-amber-500">{byStatus[col].length}</span>
              </div>
              <div className="space-y-3">
                {byStatus[col].map((t) => (
                    <div
                      key={t.id}
                      className={`rounded-md p-4 transition-colors duration-200 cursor-grab active:cursor-grabbing border ${vendorStyle(t, vColors)?.card || "bg-card border-slate-700/60 hover:border-amber-500/40"}`}
                      data-testid={`dispatch-card-${t.id}`}
                      draggable
                      onDragStart={(e) => e.dataTransfer.setData("text/plain", t.id)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono text-xs text-amber-400">{t.ticket_number}</span>
                        {canDispatch && (
                          <button data-testid={`swd-ticket-delete-${t.id}`} onClick={() => deleteTicket(t)} className="p-1 text-slate-500 hover:text-red-400 transition-colors" title="Delete ticket"><Trash2 size={14} /></button>
                        )}
                      </div>
                      {vendorStyle(t, vColors) && (
                        <span data-testid={`vendor-chip-${t.id}`} className={`inline-flex items-center px-2 py-0.5 rounded border text-[10px] font-mono uppercase tracking-wider mb-1 ${vendorStyle(t, vColors).chip}`}>
                          {vendorStyle(t, vColors).src}
                        </span>
                      )}
                      {t.driver && (
                        <span data-testid={`swd-driver-bubble-${t.id}`} className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-mono mb-1 ${((t.drivers_accepted || []).includes(t.driver) || (t.driver_accepted && !(t.drivers_accepted || []).length)) ? "bg-sky-500/15 border-sky-400/60 text-sky-600" : "bg-slate-900/60 border-slate-700 text-slate-400"}`}>
                          {t.driver}{((t.drivers_accepted || []).includes(t.driver) || (t.driver_accepted && !(t.drivers_accepted || []).length)) ? " ✓" : ""}
                        </span>
                      )}
                      <p className="text-sm font-medium text-slate-100">{t.division_name || "—"}</p>
                      {(t.pickup_location || t.destination) && (
                        <p className="text-xs text-slate-400 mt-1">{t.pickup_location || "—"} → {t.destination || "—"}</p>
                      )}
                      <div className="flex justify-between text-xs text-slate-500 mt-2 font-mono">
                        <span>{t.operator || "—"}</span>
                        <span>{[t.item, t.qty].filter((x) => x != null && x !== "").join(" × ") || "—"}</span>
                      </div>
                      {t.tags && (
                        <p className="text-[11px] mt-2 px-2 py-1 rounded bg-amber-500/10 border border-amber-500/30 text-amber-600 font-mono break-words" data-testid={`dispatch-card-tags-${t.id}`}>
                          {t.tags}
                        </p>
                      )}
                      {t.start && <p className="text-[10px] text-slate-500 mt-2 font-mono">{fmtDate(t.start)}</p>}
                      {canDispatch && (
                        <div className="flex gap-2 mt-3">
                          {NEXT[t.job_status || "Active"] && (
                            <ActionButton testId={`dispatch-advance-${t.id}`} onClick={() => setStatus(t, NEXT[t.job_status || "Active"])} className="!px-2.5 !py-1 text-xs flex-1 justify-center">
                              {NEXT[t.job_status || "Active"]} <ArrowRight size={12} />
                            </ActionButton>
                          )}
                          {t.job_status !== "Cancelled" && t.job_status !== "Completed" && (
                            <ActionButton testId={`dispatch-cancel-${t.id}`} variant="danger" onClick={() => setStatus(t, "Cancelled")} className="!px-2.5 !py-1 text-xs">
                              ✕
                            </ActionButton>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </HorizontalScroll>
      )}

      <JobTicketForm open={open} onOpenChange={setOpen} onSaved={load} defaultStatus="Assigned to Service Provider" />
    </div>
  );
}
